import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroButton,
  WalteroCard,
  WalteroTypography
} from './waltero';
import billingService from '../services/billingService';
import {
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

/**
 * Cancel Subscription Modal
 *
 * Handles subscription cancellation with warnings and impact explanation
 */
const CancelSubscriptionModal = ({
  open,
  onClose,
  subscription,
  organizationId,
  onSuccess
}) => {
  const theme = useTheme();
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setReason('');
      setConfirmText('');
      setError(null);
    }
  }, [open]);

  // Handle cancellation
  const handleCancel = async () => {
    if (confirmText !== 'CANCEL SUBSCRIPTION') {
      setError('Please type "CANCEL SUBSCRIPTION" to confirm');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await billingService.cancelSubscription(
        organizationId,
        subscription.id,
        reason || 'No reason provided'
      );
      onSuccess();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.error || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  if (!subscription) return null;

  const cancellationDate = subscription.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'the end of the current billing period';

  return (
    <Dialog
      open={open}
      onClose={() => !processing && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CancelIcon
            sx={{
              fontSize: 32,
              color: theme.palette.error.main,
              mr: 2
            }}
          />
          <Box>
            <WalteroTypography variant="h5" fontWeight="bold">
              Cancel Subscription
            </WalteroTypography>
            <Typography variant="body2" color="textSecondary">
              {subscription.subscriptionTypeName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Warning Card */}
        <WalteroCard
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.error.light + '10',
            border: `1px solid ${theme.palette.error.main}`
          }}
        >
          <Box display="flex" alignItems="start">
            <WarningIcon sx={{ color: theme.palette.error.main, mr: 2, mt: 0.5 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Are you sure you want to cancel?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Cancelling your subscription will have the following effects:
              </Typography>
            </Box>
          </Box>
        </WalteroCard>

        {/* Impact List */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          What happens when you cancel:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Service continues until period end"
              secondary={`Your sensors will remain active until ${cancellationDate}`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="No refund for remaining time"
              secondary="You've paid for the full annual period and will not receive a prorated refund"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Sensors stop working after cancellation"
              secondary="After the cancellation date, all assigned sensors will stop collecting data"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="You can reactivate anytime"
              secondary="You can undo the cancellation before the end date"
            />
          </ListItem>
        </List>

        {/* Current Subscription Details */}
        <WalteroCard sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[50] }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Current Subscription
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">
              {subscription.purchasedSensorQuantity} sensors
            </Typography>
            <Typography variant="body2">
              ${(subscription.purchasedSensorQuantity * 12).toFixed(2)}/year
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Typography variant="body2" color="textSecondary">
              Active until
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {cancellationDate}
            </Typography>
          </Box>
        </WalteroCard>

        {/* Reason for Cancellation */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Reason for cancellation (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Help us improve by telling us why you're cancelling..."
          sx={{ mb: 3 }}
        />

        {/* Confirmation Input */}
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            To confirm cancellation, please type <strong>CANCEL SUBSCRIPTION</strong> below:
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="Type CANCEL SUBSCRIPTION to confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          error={confirmText !== '' && confirmText !== 'CANCEL SUBSCRIPTION'}
          helperText={
            confirmText !== '' && confirmText !== 'CANCEL SUBSCRIPTION'
              ? 'Text must match exactly'
              : ''
          }
        />
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <WalteroButton
          onClick={onClose}
          disabled={processing}
        >
          KEEP SUBSCRIPTION
        </WalteroButton>
        <WalteroButton
          variant="contained"
          color="error"
          onClick={handleCancel}
          disabled={processing || confirmText !== 'CANCEL SUBSCRIPTION'}
          startIcon={processing ? <CircularProgress size={16} /> : <CancelIcon />}
        >
          {processing ? 'CANCELLING...' : 'CANCEL SUBSCRIPTION'}
        </WalteroButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelSubscriptionModal;